import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Modal,
  Table,
  Button,
  Space,
  Tag,
  Form,
  Input,
  Select,
  Popconfirm,
  Upload,
  message,
  Avatar,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  UploadOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import moment from "moment";

import {
  getTravelLocationListRequest,
  getCountriesRequest,
  createTravelLocationRequest,
  updateTravelLocationRequest,
  deleteTravelLocationRequest,
  restoreTravelLocationRequest,
} from "providers/TravelLocationProvider/slice";
import Helper from "utils/helpers";
import "./styles.less";

const { Option } = Select;

const TravelLocationModal = ({ visible, onClose }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [editingId, setEditingId] = useState(null);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [backgroundUrl, setBackgroundUrl] = useState(null);
  const [uploadingBg, setUploadingBg] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");

  const { list, isLoading, total, countries } = useSelector((state) => state.travelLocation);

  useEffect(() => {
    if (visible) {
      loadData();
      loadCountries();
    }
  }, [visible]);

  const loadData = (search = "", country = "") => {
    const params = {
      limit: 100,
      isDeleted: false,
    };

    if (search) {
      params.search = search;
    }

    if (country) {
      params.country = country;
    }

    dispatch(getTravelLocationListRequest(params));
  };

  const loadCountries = () => {
    dispatch(getCountriesRequest());
  };

  const handleSubmit = async (values) => {
    const data = {
      title: values.title,
      description: values.description,
      country: values.country,
      backgroundImage: backgroundUrl || undefined,
    };

    if (editingId) {
      // Update
      await dispatch(updateTravelLocationRequest({ _id: editingId, ...data }));
    } else {
      // Create
      await dispatch(createTravelLocationRequest(data));
    }

    handleCancelForm();
    // Reload data immediately after action
    loadData(searchText, selectedCountry);
  };

  const handleEdit = (record) => {
    setEditingId(record._id);
    setIsFormVisible(true);
    form.setFieldsValue({
      title: record.title,
      description: record.description,
      country: record.country,
    });

    // Set existing image
    setBackgroundUrl(record.backgroundImage || null);
  };

  const handleDelete = (id) => {
    dispatch(deleteTravelLocationRequest({ _id: id }));
    // Reload after delete
    setTimeout(() => loadData(searchText, selectedCountry), 500);
  };

  const handleRestore = (id) => {
    dispatch(restoreTravelLocationRequest({ _id: id }));
    // Reload after restore
    setTimeout(() => loadData(searchText, selectedCountry), 500);
  };

  const handleCancelForm = () => {
    setIsFormVisible(false);
    setEditingId(null);
    form.resetFields();
    setBackgroundUrl(null);
  };

  // Custom upload handler for background
  const handleUploadBackground = async (options) => {
    const { file, onSuccess, onError } = options;
    setUploadingBg(true);

    try {
      const uploadedImage = await Helper.uploadFile(
        { file, type: "BANNER" },
        (progress) => {
          console.log("Upload progress:", progress);
        }
      );

      if (uploadedImage && uploadedImage.url) {
        setBackgroundUrl(uploadedImage.url);
        onSuccess(uploadedImage, file);
        message.success("Ảnh nền đã được tải lên");
      } else {
        onError(new Error("Upload failed"));
        message.error("Không thể tải ảnh nền");
      }
    } catch (error) {
      onError(error);
      message.error("Lỗi tải ảnh: " + error.message);
    } finally {
      setUploadingBg(false);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    loadData(value, selectedCountry);
  };

  const handleCountryFilter = (value) => {
    setSelectedCountry(value);
    loadData(searchText, value);
  };

  // Get country name from code
  const getCountryName = (code) => {
    const country = countries.find(c => c.code === code);
    return country ? country.name : code;
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "travelLocationId",
      key: "travelLocationId",
      width: 80,
      render: (id) => <span className="font-weight-bold">#{id || "N/A"}</span>,
    },
    {
      title: "Ảnh nền",
      dataIndex: "backgroundImage",
      key: "backgroundImage",
      width: 100,
      align: "center",
      render: (image) =>
        image ? (
          <Avatar
            src={image}
            size={50}
            shape="square"
            icon={<GlobalOutlined />}
            className="location-bg-avatar"
          />
        ) : (
          <Avatar
            size={50}
            shape="square"
            icon={<GlobalOutlined />}
            className="location-bg-placeholder"
          />
        ),
    },
    {
      title: "Tên địa điểm",
      dataIndex: "title",
      key: "title",
      render: (title) => <strong>{title}</strong>,
    },
    {
      title: "Mô tả",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (desc) => desc || <span className="text-muted">Chưa có mô tả</span>,
    },
    {
      title: "Quốc gia",
      dataIndex: "country",
      key: "country",
      width: 150,
      render: (code) => (
        <Tag color="blue">{getCountryName(code)}</Tag>
      ),
    },
    {
      title: "Số địa điểm",
      dataIndex: "travelPlaceCount",
      key: "travelPlaceCount",
      width: 100,
      align: "center",
      render: (count) => <Tag color="green">{count || 0}</Tag>,
    },
    {
      title: "Trạng thái",
      dataIndex: "isDeleted",
      key: "isDeleted",
      width: 100,
      render: (isDeleted) => (
        <Tag color={isDeleted ? "red" : "green"}>
          {isDeleted ? "Đã xóa" : "Hoạt động"}
        </Tag>
      ),
    },
    {
      title: "Ngày tạo",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      render: (date) => date ? moment(date).format("DD/MM/YYYY HH:mm") : "-",
    },
    {
      title: "Thao tác",
      key: "action",
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Sửa
          </Button>
          {record.isDeleted ? (
            <Popconfirm
              title="Khôi phục địa điểm này?"
              onConfirm={() => handleRestore(record._id)}
              okText="Khôi phục"
              cancelText="Hủy"
            >
              <Button type="link" size="small" style={{ color: '#52c41a' }}>
                Khôi phục
              </Button>
            </Popconfirm>
          ) : (
            <Popconfirm
              title={
                <div>
                  <p>Bạn có chắc muốn xóa địa điểm này?</p>
                  {record.travelPlaceCount > 0 && (
                    <p style={{ color: '#ff4d4f', margin: 0 }}>
                      ⚠️ Có {record.travelPlaceCount} địa điểm du lịch thuộc khu vực này
                    </p>
                  )}
                </div>
              }
              onConfirm={() => handleDelete(record._id)}
              okText="Xóa"
              cancelText="Hủy"
              okButtonProps={{ danger: true }}
            >
              <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                Xóa
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Modal
      title="Quản lý khu vực du lịch"
      visible={visible}
      onCancel={onClose}
      width={1200}
      footer={null}
      className="travel-location-modal"
    >
      <div className="modal-header">
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsFormVisible(true)}
          >
            Thêm khu vực mới
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => loadData(searchText, selectedCountry)}
            loading={isLoading}
          >
            Làm mới
          </Button>
          <Input.Search
            placeholder="Tìm kiếm theo tên hoặc mô tả"
            allowClear
            style={{ width: 250 }}
            onSearch={handleSearch}
          />
          <Select
            placeholder="Lọc theo quốc gia"
            allowClear
            style={{ width: 200 }}
            onChange={handleCountryFilter}
            value={selectedCountry}
          >
            {countries.map(country => (
              <Option key={country.code} value={country.code}>
                {country.name}
              </Option>
            ))}
          </Select>
        </Space>
      </div>

      {isFormVisible && (
        <div className="form-container">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            className="travel-location-form"
          >
            <Form.Item
              name="title"
              label="Tên khu vực"
              rules={[{ required: true, message: "Vui lòng nhập tên khu vực!" }]}
            >
              <Input placeholder="VD: Miền Bắc Việt Nam" />
            </Form.Item>

            <Form.Item
              name="country"
              label="Quốc gia"
              rules={[{ required: true, message: "Vui lòng chọn quốc gia!" }]}
            >
              <Select
                placeholder="Chọn quốc gia"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
              >
                {countries.map(country => (
                  <Option key={country.code} value={country.code}>
                    {country.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="description" label="Mô tả">
              <Input.TextArea
                rows={3}
                placeholder="Nhập mô tả về khu vực này..."
              />
            </Form.Item>

            <Form.Item label="Ảnh nền">
              <Upload
                customRequest={handleUploadBackground}
                showUploadList={false}
                accept="image/*"
                maxCount={1}
              >
                <Button icon={<UploadOutlined />} loading={uploadingBg}>
                  {uploadingBg ? "Đang tải..." : "Chọn ảnh nền"}
                </Button>
              </Upload>
              {backgroundUrl && (
                <div style={{ marginTop: 8 }}>
                  <img
                    src={backgroundUrl}
                    alt="background preview"
                    style={{ width: 200, height: 120, objectFit: "cover" }}
                  />
                  <Button
                    type="link"
                    danger
                    size="small"
                    onClick={() => setBackgroundUrl(null)}
                  >
                    Xóa
                  </Button>
                </div>
              )}
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  {editingId ? "Cập nhật" : "Tạo mới"}
                </Button>
                <Button onClick={handleCancelForm}>Hủy</Button>
              </Space>
            </Form.Item>
          </Form>
        </div>
      )}

      <Table
        columns={columns}
        dataSource={list}
        loading={isLoading}
        rowKey="_id"
        pagination={{
          total,
          pageSize: 100,
          showTotal: (total) => `Tổng cộng: ${total} khu vực`,
        }}
        scroll={{ y: 400 }}
      />
    </Modal>
  );
};

export default TravelLocationModal;